import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Users,
  Building2,
  Receipt,
  UserCheck,
  Tag,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Shield,
  TrendingUp,
  Briefcase,
  ShieldAlert,
  MessageSquare
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PlatformLayout = ({ children }) => {
  const { user, logout, isSuperAdmin, isFinanceManager, isSalesManager, isSalesRep } = usePlatformAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/platform/login');
  };

  const roleColors = {
    SUPER_ADMIN: 'bg-purple-100 text-purple-700',
    FINANCE_MANAGER: 'bg-blue-100 text-blue-700',
    SALES_MANAGER: 'bg-green-100 text-green-700',
    SALES_REP: 'bg-orange-100 text-orange-700',
    SUPPORT: 'bg-gray-100 text-gray-700'
  };

  const roleLabels = {
    SUPER_ADMIN: 'Super Admin',
    FINANCE_MANAGER: 'Finance',
    SALES_MANAGER: 'Sales Manager',
    SALES_REP: 'Sales Rep',
    SUPPORT: 'Support'
  };

  // Navigation items based on role
  const navItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      href: '/platform',
      show: true
    },
    {
      label: 'Inquiries',
      icon: MessageSquare,
      href: '/platform/inquiries',
      show: true
    },
    {
      label: 'Customers',
      icon: Building2,
      href: '/platform/customers',
      show: true
    },
    {
      label: 'Revenue',
      icon: TrendingUp,
      href: '/platform/revenue',
      show: isFinanceManager
    },
    {
      label: 'Security',
      icon: ShieldAlert,
      href: '/platform/security',
      show: isSuperAdmin
    },
    {
      label: 'Sales Reps',
      icon: UserCheck,
      href: '/platform/sales-reps',
      show: isSalesManager || isSalesRep
    },
    {
      label: 'Coupons',
      icon: Tag,
      href: '/platform/coupons',
      show: isSalesManager
    },
    {
      label: 'Enterprise',
      icon: Briefcase,
      href: '/platform/enterprise',
      show: isFinanceManager
    },
    {
      label: 'Platform Users',
      icon: Users,
      href: '/platform/users',
      show: isSuperAdmin
    },
    {
      label: 'Settings',
      icon: Settings,
      href: '/platform/settings',
      show: isSuperAdmin
    }
  ].filter(item => item.show);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-slate-800 border-r border-slate-700
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
          <Link to="/platform" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">Platform</span>
          </Link>
          <button
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                  }
                `}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <Badge className={`text-xs ${roleColors[user?.role] || 'bg-slate-600 text-slate-300'}`}>
                {roleLabels[user?.role] || user?.role}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              data-testid="platform-logout"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 flex items-center justify-between px-4 lg:px-6">
          <button
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-white hidden sm:block">
              FinOrchest Platform Admin
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-700">
                  {user?.name}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-slate-800 border-slate-700">
                <DropdownMenuLabel className="text-slate-400">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-slate-300 hover:text-white hover:bg-slate-700 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default PlatformLayout;
